import { createApp } from 'vue'
import App from './App.vue'
import router from './router'
import store from './store'

import 'bootstrap/dist/css/bootstrap.min.css'
import 'bootstrap/dist/js/bootstrap.bundle.min.js'
import '@coreui/coreui/dist/css/coreui.min.css'
import 'bootstrap-icons/font/bootstrap-icons.css';
import 'easymde/dist/easymde.min.css'
import 'font-awesome/css/font-awesome.min.css'
import '@/assets/fonts/roboto/roboto.css'
import '@/assets/fonts/logo/logo-fonts.css'
import '@/assets/styles/vendor-icons.css'

import axios from 'axios'
import { resolveAuthHeaderValue, clearAuthTokens } from '@/utils/authToken'
import { apiUrl } from '@/utils/appConfig'

axios.defaults.withCredentials = false
axios.defaults.baseURL = apiUrl()

// 1. Request interceptor — registered BEFORE fetchCurrentUser, so the
//    initial /api/account/me also carries the Authorization header.
axios.interceptors.request.use(config => {
    const authHeaderValue = resolveAuthHeaderValue()
    if (authHeaderValue) {
        config.headers = config.headers || {}
        if (!config.headers.Authorization) {
            config.headers.Authorization = authHeaderValue
        }
    }
    return config
})

// 2. Response interceptor — also BEFORE fetchCurrentUser, so a 401 on
//    the very first request is handled by the same path.
//
//    A single 401 no longer logs the user out. We retry the failed request
//    once (after a short delay) to ride out transient 401s — a backend
//    restart, a brief race, a momentary gateway blip. Only a *second*,
//    consecutive 401 on the same request clears the token and redirects.
//    401s from the auth endpoints themselves (bad credentials etc.) are
//    passed straight through so the calling form can show the error.
const AUTH_EXEMPT_PATHS = [
    '/api/account/authenticate',
    '/api/account/register',
    '/api/account/forgot-password',
    '/api/account/reset-password',
]

const isAuthExemptUrl = url =>
    AUTH_EXEMPT_PATHS.some(path => String(url || '').includes(path))

const delay = ms => new Promise(resolve => setTimeout(resolve, ms))

const forceLogout = () => {
    clearAuthTokens()
    if (router.currentRoute.value.name !== 'Login') {
        router.push('/login')
    }
}

axios.interceptors.response.use(
    response => response,
    async error => {
        const status = error.response?.status
        const original = error.config

        // Not a 401, or no request to retry → propagate unchanged.
        if (status !== 401 || !original) {
            return Promise.reject(error)
        }

        // Bad credentials / bad input on auth endpoints — let the form handle it.
        if (isAuthExemptUrl(original.url)) {
            return Promise.reject(error)
        }

        // First 401 on this request: retry once before giving up.
        if (!original._retried401) {
            original._retried401 = true
            await delay(500)
            return axios(original)
        }

        // Second consecutive 401 → the session is genuinely gone.
        forceLogout()
        return Promise.reject(error)
    }
)

// 3. AFTER both interceptors are wired — fetch user and mount the app.
//    `.finally` (not `.then`) so the app mounts even on 401 — the user
//    lands on /login but the app shell renders.
store.dispatch('fetchCurrentUser').finally(() => {
    const app = createApp(App)
    app.use(store)
    app.use(router)
    app.mount('#app')
})
